import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import SaveIcon from "@mui/icons-material/Save";

import { DialogProps as MUIDialogProps, Theme } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { styled, SxProps } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import Loader from "_shared/_views/loading/loader";
import React, { ReactNode, RefObject, useCallback } from "react";

const Title = styled(DialogTitle)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1, 2),
  },
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
}));
const TitleNav = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
}));
const BackButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(1),
  zIndex: 999,
}));
const BackIcon = styled(ArrowBackIcon)(() => ({
  fontSize: "1.2em",
}));
const CloseButton = styled(IconButton)(() => ({
  zIndex: 999,
}));
const Content = styled(DialogContent)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}));

export interface DialogProps {
  MuiProps?: Partial<MUIDialogProps>;
  component: React.ReactNode;
  title?: ReactNode | string;
  contentSxProps?: SxProps<Theme>;
  titleStyle?: React.CSSProperties;
  titleTypographyStyle?: React.CSSProperties;
  hideCloseButton?: boolean;
  hideBackButton?: boolean;
  hideFullScreenButton?: boolean;
  showSaveButton?: boolean;
  disableSave?: boolean;
  saveButtonTooltip?: string;
  onBack?: () => void;
  onClose?: () => void;
  isDirty?: boolean;
  isDirtyRef?: RefObject<boolean>;
  onSave?: (saveAbleItem?: unknown, preventNavigation?: boolean) => Promise<unknown>;
  saveAbleItem?: unknown;
  resetStack?: boolean;
}

export const DialogComponent = React.forwardRef(function DialogComponent(
  props: Readonly<DialogProps>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const {
    MuiProps,
    component,
    title,
    contentSxProps,
    titleStyle,
    titleTypographyStyle,
    hideBackButton,
    hideFullScreenButton,
    hideCloseButton,
    showSaveButton,
    disableSave,
    saveButtonTooltip,
    saveAbleItem,
    isDirty,
    isDirtyRef,
    onBack,
    onClose,
    onSave,
  } = props;

  const confirm = useConfirmDialog();

  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const handleFullscreenToggle = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleCloseClicked = async () => {
    if (isDirty || isDirtyRef?.current) {
      const response = await confirm({
        title: "Discard changes?",
        message: "Are you sure you want to discard changes?",
        okButtonText: "Discard",
        alternativeOkText: disableSave ? undefined : "Save & Close",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) {
        return;
      }
      if (response === ConfirmResponse.AltOk) {
        await onSave?.(saveAbleItem, true);
        onClose?.();
        return;
      }
    }
    onClose?.();
  };

  const handleOnSave = useCallback(async () => {
    try {
      setIsSaving(true);
      await onSave?.(saveAbleItem);
    } finally {
      setIsSaving(false);
    }
  }, [onSave, saveAbleItem]);

  return (
    <Dialog
      open
      fullScreen={isSmallScreen || isFullscreen}
      ref={ref}
      disablePortal
      closeAfterTransition={false}
      {...MuiProps}
    >
      <Title style={titleStyle}>
        <TitleNav>
          {!hideBackButton && (
            <Tooltip title="Back" placement="bottom">
              <span>
                <BackButton aria-label="back" onClick={onBack} size="medium">
                  <BackIcon />
                </BackButton>
              </span>
            </Tooltip>
          )}
          <Typography style={titleTypographyStyle} variant="h5">
            {title}
          </Typography>
        </TitleNav>
        <span>
          {!hideFullScreenButton && (
            <Tooltip title={isFullscreen ? "Restore" : "Fullscreen"} placement="bottom">
              <span>
                <IconButton aria-label="full screen" onClick={handleFullscreenToggle} size="medium">
                  {isFullscreen ? <FullscreenExitIcon fontSize="medium" /> : <FullscreenIcon fontSize="medium" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {showSaveButton && (
            <Tooltip title={saveButtonTooltip} placement="bottom">
              <span>
                <IconButton
                  aria-label="Save"
                  color="primary"
                  disabled={disableSave || isSaving}
                  onClick={handleOnSave}
                  size="medium"
                >
                  {isSaving ? <Loader thickness={3} size={24} hideText /> : <SaveIcon fontSize="medium" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {!hideCloseButton && (
            <Tooltip title="Close" placement="bottom">
              <span>
                <CloseButton aria-label="close" onClick={handleCloseClicked} size="medium" edge="end">
                  <CloseIcon fontSize="large" />
                </CloseButton>
              </span>
            </Tooltip>
          )}
        </span>
      </Title>
      <Content sx={contentSxProps}>{component}</Content>
    </Dialog>
  );
});
